import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ChatText } from "@phosphor-icons/react";

const TEXTURE =
  "https://images.unsplash.com/photo-1521459467264-802e2ef3141f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwY29uY3JldGUlMjB0ZXh0dXJlfGVufDB8fHx8MTc5MDcxMjE1N3ww&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <img src={TEXTURE} alt="" className="h-[120%] w-full object-cover opacity-[0.08] mix-blend-overlay" />
      </motion.div>
      <div className="relative mx-auto max-w-6xl px-4 pb-20 pt-16 sm:px-6 sm:pb-28 sm:pt-24">
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety sm:text-sm">
          Missed-call text-back for home-services contractors
        </p>
        <h1
          data-testid="hero-headline"
          className="mt-6 font-display text-5xl font-bold uppercase leading-[1.1] tracking-tighter sm:text-6xl md:text-7xl"
        >
          <span className="block">The phone rings.</span>
          <span className="block">You're on a roof.</span>
          <span className="block text-safety">The next guy gets the job.</span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-base font-medium leading-relaxed text-gray-300 sm:text-xl"
        >
          When you can't pick up, the caller gets a text from your business in about a minute — and
          your phone buzzes with their number. They reply by text instead of phoning the next name
          on Google. Serving the Niagara Region and Hamilton.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <a
            href="tel:+19053517489"
            data-testid="hero-call-button"
            className="flex min-h-[64px] items-center justify-center gap-3 bg-safety px-8 font-display text-xl font-bold uppercase tracking-wide text-asphalt transition-[background-color,transform] duration-200 hover:bg-safetydeep active:translate-y-0.5"
          >
            <Phone weight="bold" className="h-6 w-6" aria-hidden="true" />
            Call 905-351-7489
          </a>
          <a
            href="sms:+19053517489"
            data-testid="hero-text-button"
            className="flex min-h-[64px] items-center justify-center gap-3 border-2 border-safety px-8 font-display text-xl font-bold uppercase tracking-wide text-safety transition-[background-color,color,transform] duration-200 hover:bg-safety hover:text-asphalt active:translate-y-0.5"
          >
            <ChatText weight="bold" className="h-6 w-6" aria-hidden="true" />
            Text 905-351-7489
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-6 text-sm text-smoke"
        >
          Prefer email?{" "}
          <a
            href="mailto:carlo@niagaracallback.ca"
            data-testid="hero-email-link"
            className="inline-flex min-h-[44px] items-center font-semibold text-safety underline underline-offset-4"
          >
            carlo@niagaracallback.ca
          </a>
        </motion.p>
      </div>
    </section>
  );
};
